import { Unform, Button, MaskInput } from 'components';
import {
  Modal,
  ModalContent,
  ModalOverlay,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Flex,
} from '@chakra-ui/react';
import { useCallback } from 'react';
import { openNotification } from 'utils/notification';
import { queryToObj } from 'utils/query';
import { RouteComponentProps } from 'react-router';
import { getDetailedTimeTracking } from 'services/detailedTimeTracking';
import { ReactComponent as IDownload } from 'assets/icons/download.svg';
import { reportsGenerationValidationSchema } from 'yupSchemas/budgeControl';

interface IFormData {
  periodoInicial: string;
  periodoFinal: string;
}

interface IProps {
  history: RouteComponentProps['history'];
  onClose: () => void;
  isOpen: boolean;
}

export const ModalHoursHistoric: React.FC<IProps> = ({ history, isOpen, onClose }) => {
  const handleSubmit = useCallback(async (data: IFormData): Promise<void> => {
    const monthInitialPeriod = Number(data.periodoInicial.slice(0, 2));
    const monthLastPeriod = Number(data.periodoFinal.slice(0, 2));

    const arrDataPeriodoInitial = data.periodoInicial.split('/');
    const arrDataPeriodoFinal = data.periodoFinal.split('/');

    const [monthInitial, dayInitial, yearInitial] = [
      arrDataPeriodoInitial[0],
      '01',
      ...arrDataPeriodoInitial.slice(1),
    ];
    const [monthFinal, dayFinal, yearFinal] = [
      arrDataPeriodoFinal[0],
      '01',
      ...arrDataPeriodoFinal.slice(1),
    ];

    const periodoInitial = new Date(`${monthInitial}/${dayInitial}/${yearInitial}`);
    const periodoFinal = new Date(`${monthFinal}/${dayFinal}/${yearFinal}`);

    const isInitialPeriodGreaterThanTheCurrentDate = periodoInitial > new Date();
    if (
      data.periodoInicial.slice(0, 2) === '00' ||
      data.periodoFinal.slice(0, 2) === '00' ||
      data.periodoInicial.slice(3) === '0000 ' ||
      data.periodoFinal.slice(3) === '0000'
    ) {
      openNotification('Período inicial ou final inválido.', 'error');
    } else if (periodoFinal < periodoInitial) {
      openNotification('O período final não pode ser inferior ao período inicial.', 'error');
      return;
    } else if (isInitialPeriodGreaterThanTheCurrentDate) {
      openNotification('O período inicial não deve ser maior que a data atual.', 'error');
      return;
    } else if (monthInitialPeriod > 12) {
      openNotification('Mês do período inicial está inválido!', 'error');
      return;
    } else if (monthLastPeriod > 12) {
      openNotification('Mês do período final está inválido!', 'error');
      return;
    }

    const searchData = queryToObj(history.location.search);

    try {
      const { data: res } = await getDetailedTimeTracking({
        periodoInicial: data.periodoInicial,
        periodoFinal: data.periodoFinal,
        idCliente: searchData.idCliente ? parseInt(searchData.idCliente as string) : undefined,
      });

      const url = window.URL.createObjectURL(new Blob([res]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'relatorio.xlsx');
      document.body.appendChild(link);
      link.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (err: any) {
      if (err?.message.includes('400')) {
        openNotification('Não houve horas contratadas no período desejado.', 'error');
      }
    }
  }, []);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent width="40rem">
        <Unform
          onSubmit={handleSubmit}
          validationSchema={reportsGenerationValidationSchema}
          initialData={{
            formato: 'xlsx',
          }}
        >
          <ModalHeader color="#22272D">Baixar relatório detalhado</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex gridGap="16px" mt="10px">
              <MaskInput
                name="periodoInicial"
                leftIcon="calendar"
                mask="period"
                label="Período inicial"
                placeholder="Período inicial"
                parseDate
              />
              <MaskInput
                name="periodoFinal"
                leftIcon="calendar"
                mask="period"
                label="Período final"
                placeholder="Período final"
                parseDate
              />
            </Flex>
          </ModalBody>

          <ModalFooter>
            <Flex w="100%" justify="space-between">
              <Button
                outline
                variant="secondary"
                mr={3}
                onClick={e => {
                  e.preventDefault();
                  onClose();
                }}
              >
                Cancelar
              </Button>
              <Button type="submit">
                <IDownload style={{ marginRight: '1rem' }} />
                Baixar
              </Button>
            </Flex>
          </ModalFooter>
        </Unform>
      </ModalContent>
    </Modal>
  );
};
